import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import ContactFormModal from '../ContactForm/ContactFormModal';
import {
    Target,
    TrendingUp,
    BookOpen,
    Award,
    Users,
    BarChart3,
    Clock,
    CheckCircle2,
    ArrowRight,
    Sparkles
} from 'lucide-react';

const CaseStudies = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openContactForm, setOpenContactForm] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const stats = [
        { value: '150M', label: 'Indian children in athletic development window', highlight: true },
        { value: '94%', label: 'Train under inadequate conditions' },
        { value: '70%', label: 'Assessment time reduction' },
        { value: '50%', label: 'Coach productivity increase' }
    ];

    return (
        <div className="min-h-screen bg-[#050505] selection:bg-emerald/30 selection:text-emerald font-sans">

            {/* Hero Section - Dark */}
            <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#050505]">
                {/* Background Elements */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-emerald/10 to-transparent" />
                    <div className="absolute top-1/4 right-0 w-[800px] h-[800px] bg-emerald/5 rounded-full blur-[120px]" />
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-5xl mx-auto text-center"
                    >
                        <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight uppercase">
                            VISIST.AI in Action: <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald via-neon-lime to-emerald">
                                Real-World Success Stories
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl text-gray-300 font-light leading-relaxed max-w-4xl mx-auto mb-10">
                            Right now, <span className="text-emerald font-bold">150 million Indian children</span> are in the critical 8-to-16 athletic development window, representing the world's largest untapped talent pool. Yet, a staggering <span className="text-white font-bold">94% train under inadequate conditions</span>, without access to quality coaching or the latest scientific interventions.
                        </p>

                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 mb-10">
                            <p className="text-base md:text-lg text-gray-200 leading-relaxed">
                                VISIST.AI was created to solve this systemic challenge, <span className="text-emerald font-semibold">democratizing elite-level performance intelligence</span> to ensure this talent doesn't just fade away, but flourishes through a powerful, interconnected ecosystem of three integrated products on one unified platform.
                            </p>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                            {stats.map((stat, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                                    className={`p-6 rounded-2xl ${stat.highlight ? 'bg-emerald/10 border-2 border-emerald/30' : 'bg-white/5 border border-white/10'}`}
                                >
                                    <div className={`text-4xl md:text-5xl font-black mb-2 ${stat.highlight ? 'text-emerald' : 'text-white'}`}>
                                        {stat.value}
                                    </div>
                                    <div className="text-sm text-gray-400 leading-tight">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Case Study 1: Academies - White Background */}
            <section className="py-32 bg-white relative overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-emerald-50 rounded-full blur-3xl opacity-40 -translate-y-1/2 -translate-x-1/2"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-6xl mx-auto"
                    >
                        {/* Section Header */}
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                                <Target className="w-8 h-8 text-emerald" />
                            </div>
                            <div>
                                <div className="text-sm font-bold text-emerald uppercase tracking-wider mb-1">Case Study 1.0 • For Academies</div>
                                <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                                    Building a Reputation for Excellence
                                </h2>
                            </div>
                        </div>

                        <div className="mb-8 p-8 bg-gradient-to-br from-gray-50 to-white border-l-4 border-emerald rounded-2xl">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">The Challenge</h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Modern badminton academies face a fundamental challenge that goes beyond simple competition: the lack of a standardized curriculum and scientific, data-driven interventions at the grassroots level. This results in skill assessments that are often subjective, inconsistent, and manual—varying from one coach to the next and limiting an academy's ability to scale its quality.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            <div className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald-200 transition-all duration-300 hover:shadow-xl">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                                        <Clock className="w-6 h-6 text-emerald" />
                                    </div>
                                    <div className="text-5xl font-black text-emerald">70%</div>
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-2">Assessment Time Reduction</h4>
                                <p className="text-gray-600 leading-relaxed">
                                    AI-powered evaluations dramatically reduce the time spent on manual assessments, freeing coaches to focus on high-value, on-court development.
                                </p>
                            </div>

                            <div className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald-200 transition-all duration-300 hover:shadow-xl">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-emerald" />
                                    </div>
                                    <div className="text-5xl font-black text-emerald">50%</div>
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-2">Coach Productivity Increase</h4>
                                <p className="text-gray-600 leading-relaxed">
                                    Strategic shift frees coaches from administrative work to focus on what matters most—developing athletes.
                                </p>
                            </div>
                        </div>

                        {/* Real Example */}
                        <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 rounded-3xl p-10 mb-8">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-12 h-12 rounded-xl bg-emerald text-white flex items-center justify-center flex-shrink-0">
                                    <BarChart3 className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-3">Real-World Impact</h4>
                                    <p className="text-lg text-gray-700 leading-relaxed italic">
                                        "A technical director at a leading academy used Smart Assessments and noticed a systemic weakness: players consistently scored lower on 'forehand airtoss' compared to their backhand. This data-driven insight allowed him to immediately design targeted Strength & Conditioning drills to strengthen wrist performance, a fix that would have been invisible through manual observation alone."
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Trust Indicators */}
                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            {[
                                { icon: Award, text: 'Trusted by Padukone School of Badminton' },
                                { icon: CheckCircle2, text: 'Backed by FSID at Indian Institute of Science' },
                                { icon: Sparkles, text: 'NVIDIA Inception Program Member' }
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl">
                                    <item.icon className="w-5 h-5 text-emerald flex-shrink-0" />
                                    <span className="text-sm font-semibold text-gray-700">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        <div className="p-8 bg-gray-900 text-white rounded-3xl">
                            <h4 className="text-2xl font-bold mb-4">The Result</h4>
                            <p className="text-lg text-gray-200 leading-relaxed">
                                By leveraging objective data, VISIST.AI transforms an academy into a verifiable center of excellence, where proven results, not just opinions, drive word-of-mouth marketing and sustainable growth. Professional, visual reports give parents unprecedented visibility, building deep trust and turning satisfied parents into vocal advocates.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Case Study 2: Players - Dark Background */}
            <section className="py-32 bg-[#050505] relative overflow-hidden">
                <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-emerald/5 rounded-full blur-[120px] -translate-y-1/2"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-6xl mx-auto"
                    >
                        {/* Section Header */}
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-emerald/10 border border-emerald/20 flex items-center justify-center">
                                <TrendingUp className="w-8 h-8 text-emerald" />
                            </div>
                            <div>
                                <div className="text-sm font-bold text-emerald uppercase tracking-wider mb-1">Case Study 2.0 • For Players</div>
                                <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                                    Gaining the Competitive Edge
                                </h2>
                            </div>
                        </div>

                        <div className="mb-8 p-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
                            <h3 className="text-2xl font-bold text-white mb-4">The Scenario</h3>
                            <p className="text-lg text-gray-300 leading-relaxed">
                                An athlete preparing for a high-stakes tournament final has access to match footage but faces the universal challenge of extracting actionable intelligence. Manually reviewing hours of video is fatiguing and often fails to reveal the subtle patterns that decide a match.
                            </p>
                        </div>

                        {/* Features Grid */}
                        <div className="grid md:grid-cols-2 gap-6 mb-12">
                            {[
                                {
                                    title: 'Heatmap Analysis',
                                    description: 'Visualize court dominance and identify positional weaknesses with precision heatmaps.'
                                },
                                {
                                    title: 'Rally Breakdowns',
                                    description: 'Granular analysis of winning and losing points, revealing tactical patterns.'
                                },
                                {
                                    title: 'Phase-Wise Performance',
                                    description: 'Understand how momentum shifts throughout the match with detailed phase analysis.'
                                },
                                {
                                    title: 'Ask VISIST Feature',
                                    description: 'Query the data conversationally: "Which was the longest rally?" Get instant, clear answers.'
                                }
                            ].map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.6 }}
                                    className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-emerald/30 transition-all duration-300"
                                >
                                    <h4 className="text-xl font-bold text-white mb-3">{feature.title}</h4>
                                    <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            ))}
                        </div>

                        <div className="bg-gradient-to-br from-emerald/10 to-emerald/5 border-2 border-emerald/20 rounded-3xl p-10">
                            <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                                <CheckCircle2 className="w-8 h-8 text-emerald" />
                                The Competitive Advantage
                            </h4>
                            <p className="text-lg text-gray-200 leading-relaxed mb-6">
                                Armed with a granular understanding of both their own and their opponent's tendencies, the player and their coach can develop a precise, evidence-based game plan. They can design drills to exploit the opponent's weaknesses and reinforce their own strengths, turning abstract observations into a concrete strategy for victory.
                            </p>
                            <p className="text-gray-300 leading-relaxed">
                                This strategic, match-by-match analysis is automatically archived in the athlete's Smart Journal, allowing them, their parents, and coaches to track tactical development over an entire season.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Case Study 3: Parents - White Background */}
            <section className="py-32 bg-white relative overflow-hidden">
                <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-emerald-50 rounded-full blur-3xl opacity-40 translate-y-1/2 translate-x-1/2"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-6xl mx-auto"
                    >
                        {/* Section Header */}
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                                <BookOpen className="w-8 h-8 text-emerald" />
                            </div>
                            <div>
                                <div className="text-sm font-bold text-emerald uppercase tracking-wider mb-1">Case Study 3.0 • For Parents</div>
                                <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                                    Tracking a Year of Growth
                                </h2>
                            </div>
                        </div>

                        <div className="mb-8 p-8 bg-gradient-to-br from-gray-50 to-white border-l-4 border-emerald rounded-2xl">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">The Parent's Challenge</h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Every parent wants to support their child's athletic journey, but they often struggle to track long-term progress, understand coaching feedback, and see a clear return on their significant investment in training.
                            </p>
                        </div>

                        {/* Benefits Grid */}
                        <div className="grid md:grid-cols-3 gap-6 mb-12">
                            {[
                                {
                                    icon: Users,
                                    title: 'Centralized Hub',
                                    description: 'All training and match videos in one secure, accessible location.'
                                },
                                {
                                    icon: BarChart3,
                                    title: 'Progress Tracking',
                                    description: 'Data-driven reports showing tangible improvement over time.'
                                },
                                {
                                    icon: BookOpen,
                                    title: 'Winning Portfolio',
                                    description: 'A living document of milestones from technique mastery to tournament success.'
                                }
                            ].map((benefit, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.6 }}
                                    className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald-200 transition-all duration-300 hover:shadow-xl text-center"
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                                        <benefit.icon className="w-8 h-8 text-emerald" />
                                    </div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                                </motion.div>
                            ))}
                        </div>

                        <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 rounded-3xl p-10 mb-8">
                            <h4 className="text-2xl font-bold text-gray-900 mb-4">The Transformation</h4>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                The Smart Journal transforms the parent's role from passive spectator to active participant. The platform's data-driven progress reports empower parents to have more meaningful, informed conversations with both their child and their coach, discussing specific insights rather than relying on general observations.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                This enhanced engagement fosters a stronger family commitment to the sport. It provides <span className="font-bold text-emerald">indisputable proof of progress</span>, ensuring their investment in their child's passion is delivering tangible results.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Bottom CTA - Emerald Gradient */}
            <section className="relative py-24 bg-gradient-to-br from-emerald via-emerald-600 to-emerald-700 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }}></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
                            Ready to write your success story?
                        </h2>
                        <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed">
                            Join academies, players, and parents who are transforming athletic development with VISIST.AI
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => navigate('/products')}
                                className="group px-6 py-3 bg-white text-emerald font-bold text-base rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-white/50 flex items-center gap-2"
                            >
                                Explore Products
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={() => setOpenContactForm(true)}
                                className="px-6 py-3 bg-white/10 hover:bg-white/20 border-2 border-white text-white font-bold text-base rounded-full transition-all duration-300 backdrop-blur-sm"
                            >
                                Contact Us
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            <ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />

        </div>
    );
};

export default CaseStudies;
